// ═══════════════════════════════════════════════════════════════
// SHELTER — Public Family Reunification Request (no auth required)
// Allows the public to report a missing person and request
// reunification. Optionally pass ?incident=<id> to scope.
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Paper, Title, Text, TextInput, Textarea, Button,
  Stack, SimpleGrid, Divider, ThemeIcon, Alert,
} from '../../components/gab/GabComponents';
import { IconHeartHandshake, IconShieldCheck, IconInfoCircle } from '@tabler/icons-react';
import { createRecord, fetchTable, TABLES, FIELDS } from '../../lib/api-switch';
import { encodeReuniDesc } from '../../store/shelterStore';
import { scoreFullMatch } from '../../lib/reunificationMatch';
import { formatPhone } from '../../lib/phoneFormat';
import { primary } from '../../lib/colors';

export default function PublicReunification() {
  const [searchParams] = useSearchParams();
  const incidentId = searchParams.get('incident') || '';

  // Missing person
  const [missingName, setMissingName] = useState('');
  const [missingDob, setMissingDob] = useState('');
  const [missingAge, setMissingAge] = useState('');
  const [missingPhone, setMissingPhone] = useState('');
  const [description, setDescription] = useState('');
  const [lastKnownLocation, setLastKnownLocation] = useState('');
  const [lastKnownDate, setLastKnownDate] = useState('');
  const [relationship, setRelationship] = useState('');

  // Requester
  const [requestedByName, setRequestedByName] = useState('');
  const [requestedByPhone, setRequestedByPhone] = useState('');
  const [requestedByEmail, setRequestedByEmail] = useState('');

  // State
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [refCode, setRefCode] = useState('');
  const [possibleMatch, setPossibleMatch] = useState(false);

  // Optional: load incident name for display
  const [incidentName, setIncidentName] = useState('');
  useEffect(() => {
    if (!incidentId) return;
    (async () => {
      try {
        const rows = await fetchTable(TABLES.incidents);
        const row = rows.find(r => String(r.record_id) === incidentId || String(r.id) === incidentId);
        if (row) setIncidentName(String((row as any)[FIELDS.incidents.name] || ''));
      } catch { /* ignore */ }
    })();
  }, [incidentId]);

  const reset = () => {
    setMissingName(''); setMissingDob(''); setMissingAge(''); setMissingPhone('');
    setDescription(''); setLastKnownLocation(''); setLastKnownDate(''); setRelationship('');
    setRequestedByName(''); setRequestedByPhone(''); setRequestedByEmail('');
    setSubmitted(false); setRefCode(''); setPossibleMatch(false);
  };

  const handleSubmit = async () => {
    if (!missingName.trim() || !requestedByName.trim()) return;
    setSubmitting(true);
    try {
      const F = FIELDS.shelterReunification;
      // Encode extra detail (lastKnownLocation, lastKnownDate) into the description
      const encodedDesc = encodeReuniDesc(
        `${description}${lastKnownLocation ? '\nLast known: ' + lastKnownLocation : ''}${lastKnownDate ? ' on ' + lastKnownDate : ''}`,
        missingDob, missingPhone, missingAge, 'public'
      );

      const fields: Array<[string, string]> = [
        [F.requestorName, requestedByName.trim()],
        [F.requestorPhone, requestedByPhone],
        [F.requestorEmail, requestedByEmail],
        [F.requestorRelationship, relationship],
        [F.missingPersonName, missingName.trim()],
        [F.missingPersonDescription, encodedDesc],
        [F.status, 'searching'],
        [F.notes, `Incident: ${incidentId}`],
      ];
      await createRecord(TABLES.shelterReunification, fields);

      // Best-effort fuzzy match against existing survivors (no PII revealed)
      try {
        const survivorRows = await fetchTable(TABLES.survivors);
        const FS = FIELDS.survivors;
        let scopedSurvivors = survivorRows;
        if (incidentId) {
          scopedSurvivors = survivorRows.filter(s => String((s as any)[FS.incidentId] ?? '') === incidentId);
        }
        const q = { name: missingName, dob: missingDob, phone: missingPhone, age: missingAge };
        let bestConfidence = 0;
        for (const s of scopedSurvivors) {
          const res = scoreFullMatch(q, s as any);
          if (res && res.confidence > bestConfidence) bestConfidence = res.confidence;
        }
        if (bestConfidence >= 75) setPossibleMatch(true);
      } catch { /* ignore — match check is best-effort */ }

      setRefCode(`RU-${Date.now().toString(36).toUpperCase()}`);
      setSubmitted(true);
    } catch (err) {
      console.error('Reunification request failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Box mih="100vh" style={{
        background: 'linear-gradient(135deg, var(--color-primary-900) 0%, var(--color-primary-700) 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}>
        <Paper shadow="lg" radius="md" p="xl" maw={520} w="100%">
          <Stack align="center" gap="md">
            <ThemeIcon size={64} radius="xl" variant="light" color="var(--color-success)">
              <IconShieldCheck size={36} />
            </ThemeIcon>
            <Title order={3} ta="center" c="#07963F">Request Submitted</Title>
            <Text ta="center" c="dimmed">
              Your reunification request for <strong>{missingName}</strong> has been received.
              A reunification specialist will review it and contact you at the number or email you provided.
            </Text>

            <Paper p="md" bg="gray.0" radius="md" withBorder w="100%">
              <Text size="xs" c="dimmed" ta="center">Reference Code</Text>
              <Text fw={700} size="xl" ta="center" ff="monospace" c={primary[900]} mt={4}>
                {refCode}
              </Text>
              <Text size="xs" c="dimmed" ta="center" mt={4}>
                Save this code and reference it when calling the shelter.
              </Text>
            </Paper>

            {possibleMatch && (
              <Alert color="var(--color-primary-700)" icon={<IconInfoCircle size={16} />} w="100%">
                <Text size="sm" fw={600}>We may have a possible match.</Text>
                <Text size="xs" c="dimmed" mt={4}>
                  A reunification specialist will reach out as soon as possible to verify.
                  For privacy and safety, we cannot share details until identity is confirmed.
                </Text>
              </Alert>
            )}

            <Button variant="light" onClick={reset}>Submit Another Request</Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box mih="100vh" style={{
      background: 'linear-gradient(135deg, #f0f4f8 0%, #d9e2ec 100%)',
      padding: 24,
    }}>
      <Paper shadow="md" radius="md" p="xl" maw={720} mx="auto">
        <Stack align="center" gap="xs" mb="lg">
          <ThemeIcon size={48} radius="xl" variant="light" color="var(--data-viz-100)">
            <IconHeartHandshake size={24} />
          </ThemeIcon>
          <Title order={3} c={primary[900]}>Family Reunification Request</Title>
          <Text size="sm" c="dimmed" ta="center" maw={520}>
            If you are searching for a missing family member or friend, please complete this form.
            A reunification specialist will review your request and contact you.
            {incidentName && <> Active incident: <strong>{incidentName}</strong>.</>}
          </Text>
        </Stack>

        <Alert color="#C68700" icon={<IconInfoCircle size={16} />} mb="lg">
          <Text size="xs">
            <strong>If this is a life-threatening emergency, call 911 immediately.</strong>
            {' '}This form is for non-emergency reunification assistance only.
          </Text>
        </Alert>

        <Divider mb="md" label="Missing Person" labelPosition="center" />

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" mb="sm">
          <TextInput label="Full Name *" value={missingName}
            onChange={e => setMissingName(e.target.value)} required />
          <TextInput label="Your Relationship" value={relationship}
            placeholder="Spouse, parent, friend..."
            onChange={e => setRelationship(e.target.value)} />
        </SimpleGrid>

        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm" mb="sm">
          <TextInput label="Date of Birth" type="date" value={missingDob}
            onChange={e => setMissingDob(e.target.value)} />
          <TextInput label="Approximate Age" type="number"
            placeholder="if DOB unknown" value={missingAge}
            onChange={e => setMissingAge(e.target.value)} />
          <TextInput label="Their Phone Number" value={missingPhone}
            placeholder="(555) 123-4567"
            onChange={e => setMissingPhone(formatPhone(e.target.value))} />
        </SimpleGrid>

        <Textarea label="Description"
          placeholder="Physical description, clothing last seen wearing, distinguishing features..."
          value={description} onChange={e => setDescription(e.target.value)}
          autosize minRows={2} mb="sm" />

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" mb="md">
          <TextInput label="Last Known Location" value={lastKnownLocation}
            placeholder="Address, neighborhood, or area"
            onChange={e => setLastKnownLocation(e.target.value)} />
          <TextInput label="Last Known Date" type="date" value={lastKnownDate}
            onChange={e => setLastKnownDate(e.target.value)} />
        </SimpleGrid>

        <Divider mb="md" label="Your Contact Information" labelPosition="center" />

        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm" mb="lg">
          <TextInput label="Your Name *" value={requestedByName}
            onChange={e => setRequestedByName(e.target.value)} required />
          <TextInput label="Your Phone" value={requestedByPhone}
            placeholder="(555) 123-4567"
            onChange={e => setRequestedByPhone(formatPhone(e.target.value))} />
          <TextInput label="Your Email" type="email" value={requestedByEmail}
            onChange={e => setRequestedByEmail(e.target.value)} />
        </SimpleGrid>

        <Button fullWidth size="lg" onClick={handleSubmit} loading={submitting}
          disabled={!missingName.trim() || !requestedByName.trim()}>
          Submit Reunification Request
        </Button>

        <Text size="xs" c="dimmed" ta="center" mt="md">
          Information you provide will be shared with reunification specialists only.
          <br />OpenGov Emergency Management Center
        </Text>
      </Paper>
    </Box>
  );
}
